import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth.js';
import { ThemeToggle } from '../theme/ThemeToggle.js';

/**
 * P23 Batch F (rev 3) — Public landing page, restyled to "Soft Industrial
 * Console".
 *
 * Scrolling marketing page for `/`. Player-facing copy, no engineering
 * jargon. Sells the fantasy ("run a galactic trade empire") rather than
 * the architecture. Flat, warm surfaces driven by theme tokens so it reads
 * correctly in both light and dark.
 *
 * Sections:
 *   1. Sticky top nav (logo + theme toggle + sign in/up)
 *   2. Hero — full viewport, dramatic. Seal, tagline, primary CTA, plus
 *      a live-feel ticker mock anchored to the bottom.
 *   3. Three pillars with miniature visual elements (belt animation,
 *      sparkline, faction stripe).
 *   4. Six-factions grid — every megacorp with one-line bio + accent.
 *   5. Tier progression visual — T1 → T5 ladder.
 *   6. Final "ship out" CTA.
 *   7. Footer with public-content links.
 *
 * Already-authenticated visitors are forwarded to `/play` so the
 * marketing page never blocks a returning player.
 */
export function LandingPage() {
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (status === 'authenticated' && user) {
      navigate('/play', { replace: true });
    }
  }, [status, user, navigate]);

  if (status === 'unknown' || status === 'loading' || (status === 'authenticated' && user)) {
    return <div className="min-h-screen bg-bg" />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg text-fg">
      <LandingStyles />
      <Backdrop />

      <TopNav />
      <Hero />
      <PillarsSection />
      <FactionsSection />
      <TierLadderSection />
      <FinalCtaSection />
      <Footer />
    </div>
  );
}

// ── Page-local CSS ──────────────────────────────────────────────────────

function LandingStyles() {
  return (
    <style>{`
      @keyframes lp-march {
        from { stroke-dashoffset: 0; }
        to   { stroke-dashoffset: -32; }
      }
      @keyframes lp-belt-item {
        from { transform: translateX(0); }
        to   { transform: translateX(120px); }
      }
      @keyframes lp-tape {
        from { transform: translateX(0); }
        to   { transform: translateX(-50%); }
      }
      @keyframes lp-fade-in-up {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .lp-fade-up { animation: lp-fade-in-up 0.7s cubic-bezier(.34,1.3,.5,1) both; }
      .lp-card {
        background: rgb(var(--c-panel));
        border: 1px solid rgb(var(--c-border));
        border-radius: var(--r-lg, 12px);
        box-shadow: var(--shadow-sm);
      }
      .lp-divider {
        height: 1px;
        background: rgb(var(--c-border));
      }
    `}</style>
  );
}

// ── Background — soft warm wash + faint dotted grid ──────────────────────

function Backdrop() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            'radial-gradient(1100px 560px at 80% -8%, rgb(var(--c-panel-3)) 0%, transparent 58%),' +
            'radial-gradient(900px 600px at 12% 8%, rgb(var(--c-accent) / 0.06), transparent 60%),' +
            'radial-gradient(900px 700px at 50% 108%, rgb(var(--c-accent-2) / 0.05), transparent 65%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-50"
        style={{
          backgroundImage:
            'radial-gradient(rgb(var(--c-border) / 0.55) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
    </>
  );
}

// ── Brand seal — flat terracotta tile (tokenized) ───────────────────────

function Seal({ size = 38 }: { size?: number }) {
  return (
    <div
      className="grid flex-none place-items-center rounded-xl bg-accent text-white shadow-soft-sm"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg
        width={size * 0.56}
        height={size * 0.56}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z" />
        <path d="M12 7v10M7.5 9.5l9 5M16.5 9.5l-9 5" />
      </svg>
    </div>
  );
}

// ── Sticky top nav ──────────────────────────────────────────────────────

function TopNav() {
  return (
    <nav
      aria-label="Primary"
      className="sticky top-0 z-30 border-b border-line bg-panel/80 shadow-soft-sm backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-3">
        <Link to="/" className="group flex items-center gap-3">
          <Seal />
          <span className="text-[18px] font-extrabold leading-none tracking-tight text-fg transition-colors group-hover:text-accent">
            Stellar Freight Co.
          </span>
        </Link>
        <span className="ml-6 hidden items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-muted md:inline-flex">
          <span className="led text-good" />
          <span>Charter network online</span>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/leaderboards"
            className="hidden rounded-md px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2 transition-colors hover:text-fg sm:inline-block"
          >
            Leaderboards
          </Link>
          <Link
            to="/corps"
            className="hidden rounded-md px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2 transition-colors hover:text-fg sm:inline-block"
          >
            Corps directory
          </Link>
          <ThemeToggle className="mx-1" />
          <Link
            to="/login"
            className="rounded-md border border-line px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-fg transition-colors hover:border-line-strong"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="rounded-md bg-accent px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-white transition-all hover:brightness-105"
          >
            Sign up
          </Link>
        </div>
      </div>
    </nav>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative flex min-h-[86vh] flex-col items-center justify-center px-6 pb-32 pt-14">
      <div className="lp-fade-up flex max-w-4xl flex-col items-center">
        <Seal size={86} />

        <div className="mt-8 mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-accent">
          <span className="led text-accent" />
          <span>Open charters · Rim Sector · 2671 ORB</span>
        </div>

        <h1 className="text-center text-[44px] font-extrabold leading-[0.98] tracking-tight text-fg sm:text-[60px] md:text-[78px]">
          Sign your charter.
          <br />
          <span className="text-accent">Run a galaxy.</span>
        </h1>

        <p className="mt-6 max-w-2xl text-center text-[15px] leading-relaxed text-fg-2 md:text-[16px]">
          From a single mining drill on a forgotten asteroid to a five-faction
          freight empire. Build factories, route drones, and out-trade the
          megacorps for the same contracts on the same shared market — every
          other charter on the network is racing for the same orders you are.
        </p>

        <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row">
          <Link
            to="/signup"
            className="group relative rounded-md bg-accent px-7 py-3 text-[14px] font-bold tracking-wide text-white transition-all hover:-translate-y-0.5 hover:brightness-105"
          >
            <span>Register a new charter</span>
            <span
              aria-hidden
              className="absolute -right-3 -top-2 rounded-full bg-good px-2 py-0.5 text-[9px] font-bold text-white shadow-soft-sm"
            >
              ₡120 starter
            </span>
          </Link>
          <Link
            to="/login"
            className="rounded-md border border-line px-6 py-3 text-[13px] font-bold tracking-wide text-fg transition-colors hover:border-line-strong"
          >
            Sign in to charter
          </Link>
        </div>

        <p className="mt-5 max-w-md text-center font-mono text-[11px] text-muted">
          Free to play in your browser. No download. Your station ticks while
          you're away — accrued credits land on your next login.
        </p>
      </div>

      {/* Live ticker pinned to the bottom of the hero — sells the
          "shared universe" feel without making it abstract. */}
      <div className="absolute bottom-0 left-0 right-0">
        <LiveTicker />
      </div>
    </section>
  );
}

// ── Live activity ticker — scrolling fake-but-plausible feed ─────────────

function LiveTicker() {
  type TickerItem = {
    faction: string;
    amount: number;
    who: string;
    good: string;
    tone: 'accent' | 'accent-2' | 'fg' | 'good' | 'bad' | 'warn';
    ai?: boolean;
  };
  const items: TickerItem[] = [
    { faction: 'Helion Dynamics',  amount: 1840, who: 'Bremen Forge Cooperative', good: 'Plasma Cell ×8',    tone: 'accent' },
    { faction: 'Argent Foundries', amount: 2400, who: 'Ironreach Holdings',       good: 'Iron Ingot ×40',    tone: 'warn' },
    { faction: 'Tycho Industrial', amount:  860, who: 'AI · Tycho Bulk-Lift',     good: 'Copper Wire ×30',   tone: 'fg',   ai: true },
    { faction: 'Meridian Biotech', amount: 3120, who: 'Verdant Charter LLC',      good: 'Antibiotic ×12',    tone: 'good' },
    { faction: 'Vega Combine',     amount:  720, who: 'AI · Salvage Run-7',       good: 'Refined Fuel ×18',  tone: 'bad',   ai: true },
    { faction: 'CTA',              amount:  540, who: 'Aurora Logistics Corp',    good: 'Iron Ore ×60',      tone: 'accent-2' },
    { faction: 'Helion Dynamics',  amount: 5200, who: 'Coronet Heavy Industries', good: 'Fusion Precursor ×4', tone: 'accent' },
    { faction: 'Argent Foundries', amount: 1980, who: 'AI · Anvil-Two',           good: 'Superalloy ×6',     tone: 'warn', ai: true },
  ];
  // Duplicate so the marquee loop is seamless.
  const doubled = [...items, ...items];
  const toneClass: Record<TickerItem['tone'], string> = {
    accent: 'text-accent',
    'accent-2': 'text-accent-2',
    good: 'text-good',
    bad: 'text-bad',
    warn: 'text-warn',
    fg: 'text-fg',
  };
  return (
    <div className="overflow-hidden border-t border-line bg-panel/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-2.5">
        <span className="flex shrink-0 items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-bad">
          <span className="led text-bad" />
          LIVE
        </span>
        <span className="hidden shrink-0 text-[10px] font-bold uppercase tracking-[0.14em] text-muted md:inline">
          Recent captures · Bid Board
        </span>
        <div className="relative h-6 flex-1 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 flex items-center gap-8 whitespace-nowrap"
            style={{ width: 'max-content', animation: 'lp-tape 60s linear infinite' }}
          >
            {doubled.map((t, i) => (
              <span key={i} className="flex shrink-0 items-center gap-2 font-mono text-[11px]">
                <span className={`font-bold ${toneClass[t.tone]}`}>{t.faction}</span>
                <span className="text-muted">·</span>
                <span className="text-fg">{t.good}</span>
                <span className="text-muted">→</span>
                <span className="font-bold tabular-nums text-accent">
                  ₡{t.amount.toLocaleString()}
                </span>
                <span className="text-muted">claimed by</span>
                <span className={t.ai ? 'italic text-muted' : 'text-fg'}>
                  {t.who}
                  {t.ai && (
                    <span className="ml-1 rounded border border-line px-1 py-px text-[8px] not-italic text-muted">
                      AI
                    </span>
                  )}
                </span>
                <span className="text-line">│</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pillars section — three cards with mini visuals ──────────────────────

function PillarsSection() {
  return (
    <section className="relative mx-auto max-w-7xl px-6 py-20">
      <SectionHeader
        eyebrow="The loop"
        title="Build, route, trade, climb."
        body="No tutorials hiding the systems. The full chain runs on day one — drill, smelt, route, sell — and grows with you."
      />
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
        <PillarCard
          tone="accent"
          stamp="01"
          title="Belts now, drones later"
          body="Lay conveyors between mining drills, smelters, and your trade port. Items pop along the strip in real time. Once you've earned the research, drones unlock for fluids, gases, and the long late-game routes."
        >
          <BeltMiniVisual />
        </PillarCard>
        <PillarCard
          tone="good"
          stamp="02"
          title="The market moves with you"
          body="Every charter on the network sells into the same prices. Flood the board with iron ingots and the price drops for everyone. Hoard a rare T3 good and watch the premium climb. Read the ticker, time the sale."
        >
          <SparklineMiniVisual />
        </PillarCard>
        <PillarCard
          tone="accent-2"
          stamp="03"
          title="Six factions, one bid board"
          body="CTA + five megacorps post premium contracts on a shared pool. Click fast or watch a Helion charter steal it. Climb a faction's rep for fatter payouts and unlock their signature trade lanes."
        >
          <FactionStripMiniVisual />
        </PillarCard>
      </div>
    </section>
  );
}

function PillarCard({
  tone,
  stamp,
  title,
  body,
  children,
}: {
  tone: 'accent' | 'good' | 'accent-2';
  stamp: string;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  const accentText =
    tone === 'accent' ? 'text-accent' : tone === 'good' ? 'text-good' : 'text-accent-2';
  const stripe =
    tone === 'accent'
      ? 'border-t-accent'
      : tone === 'good'
        ? 'border-t-good'
        : 'border-t-accent-2';
  return (
    <article
      className={`group relative overflow-hidden rounded-lg border border-t-[3px] border-line bg-panel p-6 shadow-soft-sm transition-all hover:-translate-y-1 hover:shadow-soft ${stripe}`}
    >
      <div className="mb-4 flex items-baseline gap-3">
        <span className={`font-mono text-[28px] font-bold leading-none ${accentText}`}>
          {stamp}
        </span>
        <span className="lp-divider flex-1" />
      </div>
      <h3 className="mb-3 text-[16px] font-extrabold tracking-tight text-fg">{title}</h3>
      <p className="mb-5 text-[13px] leading-relaxed text-fg-2">{body}</p>
      <div className="flex h-24 items-center justify-center overflow-hidden rounded-md border border-line bg-panel-2 p-3">
        {children}
      </div>
    </article>
  );
}

function BeltMiniVisual() {
  // Three chevron strips with two scrolling resource dots. Stroke colors use
  // CSS vars so the strip reads correctly in light and dark.
  return (
    <svg viewBox="0 0 200 60" className="h-full w-full" aria-hidden>
      <rect
        x="10"
        y="22"
        width="180"
        height="16"
        fill="rgb(var(--c-panel-3))"
        stroke="rgb(var(--c-border-strong))"
        strokeWidth="0.6"
      />
      <line x1="10" y1="22" x2="190" y2="22" stroke="rgb(var(--c-accent))" strokeWidth="0.7" strokeDasharray="4 6" opacity="0.6" />
      <line x1="10" y1="38" x2="190" y2="38" stroke="rgb(var(--c-accent))" strokeWidth="0.7" strokeDasharray="4 6" opacity="0.6" />
      {[0, 30, 60, 90, 120, 150].map((x) => (
        <path
          key={x}
          d={`M ${x + 10} 26 L ${x + 17} 30 L ${x + 10} 34`}
          fill="none"
          stroke="rgb(var(--c-accent))"
          strokeWidth="1.4"
          opacity="0.8"
          style={{ strokeDasharray: '32 0', animation: 'lp-march 1.6s linear infinite' }}
        />
      ))}
      <g style={{ animation: 'lp-belt-item 3.2s linear infinite' }}>
        <circle cx="20" cy="30" r="3.6" fill="rgb(var(--c-accent))" />
      </g>
      <g style={{ animation: 'lp-belt-item 3.2s linear infinite -1.6s' }}>
        <circle cx="20" cy="30" r="3.6" fill="rgb(var(--c-good))" />
      </g>
    </svg>
  );
}

function SparklineMiniVisual() {
  // Pseudo price chart. Sparkline + price + delta.
  const path =
    'M 10 30 L 26 28 L 38 32 L 50 22 L 62 26 L 74 18 L 86 24 L 98 16 L 110 21 L 124 15 L 138 20 L 152 12 L 166 17 L 182 9';
  return (
    <svg viewBox="0 0 200 60" className="h-full w-full" aria-hidden>
      <line x1="10" y1="48" x2="190" y2="48" stroke="rgb(var(--c-border))" strokeWidth="0.4" strokeDasharray="2 3" />
      <path d={path} fill="none" stroke="rgb(var(--c-good))" strokeWidth="1.6" />
      <path d={`${path} L 182 50 L 10 50 Z`} fill="rgb(var(--c-good))" opacity="0.12" />
      <text x="10" y="60" fontFamily="'Space Mono', ui-monospace, monospace" fontSize="6" fill="rgb(var(--c-muted))">
        IRON INGOT · 24h
      </text>
      <text x="148" y="60" fontFamily="'Space Mono', ui-monospace, monospace" fontSize="6" fill="rgb(var(--c-good))">
        ₡8 · +14%
      </text>
    </svg>
  );
}

function FactionStripMiniVisual() {
  // Six tiny hex emblems in their faction colours (tokenized CSS vars).
  const accents = [
    { color: 'var(--f-cta)', label: 'CTA' },
    { color: 'var(--f-helion)', label: 'HEL' },
    { color: 'var(--f-argent)', label: 'ARG' },
    { color: 'var(--f-merid)', label: 'MER' },
    { color: 'var(--f-tycho)', label: 'TYC' },
    { color: 'var(--f-vega)', label: 'VEG' },
  ];
  return (
    <div className="flex items-center gap-3 px-2">
      {accents.map((a, i) => (
        <div key={i} className="flex flex-col items-center gap-1">
          <svg width="28" height="32" viewBox="0 0 28 32" aria-hidden>
            <polygon
              points="14,2 26,9 26,23 14,30 2,23 2,9"
              fill="rgb(var(--c-panel-2))"
              stroke={`rgb(${a.color})`}
              strokeWidth="1.6"
            />
            <circle cx="14" cy="16" r="3.2" fill={`rgb(${a.color})`} opacity="0.9" />
          </svg>
          <span className="font-mono text-[8.5px] tracking-wider text-muted">{a.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Factions section ────────────────────────────────────────────────────

function FactionsSection() {
  const factions = [
    {
      name: 'Commonwealth Trade Authority',
      tagline: 'The regulator',
      blurb: 'Sets standards, certifies charters, and drops the blue seal on every legal contract. Generalist orders, no rivals.',
      accent: 'var(--f-cta)',
      shape: 'cta',
    },
    {
      name: 'Helion Dynamics',
      tagline: 'Energy megacorp',
      blurb: 'Fusion cores, plasma cells, pristine reactor output. Pays a premium for fuel deliveries. Rivals Vega.',
      accent: 'var(--f-helion)',
      shape: 'helion',
    },
    {
      name: 'Argent Foundries',
      tagline: 'Metals megacorp',
      blurb: 'Alloys, superalloys, precision bearings. Bulk material orders with tight deadlines. Rivals Tycho.',
      accent: 'var(--f-argent)',
      shape: 'argent',
    },
    {
      name: 'Meridian Biotech',
      tagline: 'Biotech megacorp',
      blurb: 'Cultures, pharmaceuticals, grown goods. Pays out research points alongside credits. Rivals CTA.',
      accent: 'var(--f-merid)',
      shape: 'meridian',
    },
    {
      name: 'Tycho Industrial',
      tagline: 'Logistics megacorp',
      blurb: 'Routes, contracts, cold chains. Their freight tariffs hit Argent hardest. Rivals Argent.',
      accent: 'var(--f-tycho)',
      shape: 'tycho',
    },
    {
      name: 'Vega Combine',
      tagline: 'Salvage megacorp',
      blurb: 'Reconditioned parts, recycled alloys, half-price with a warranty asterisk. Rivals Helion.',
      accent: 'var(--f-vega)',
      shape: 'vega',
    },
  ];
  return (
    <section className="relative mx-auto max-w-7xl px-6 py-20">
      <SectionHeader
        eyebrow="Sign with one. Antagonise the rest."
        title="Six megacorps. One galaxy."
        body="Pick your starting faction at signup. Your rep with each megacorp shapes the contracts they offer, the prices they pay, and which rivals start hating you. Defect later if the politics shift — for a price."
      />
      <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {factions.map((f) => (
          <FactionCard key={f.shape} {...f} />
        ))}
      </div>
    </section>
  );
}

function FactionCard({
  name,
  tagline,
  blurb,
  accent,
}: {
  name: string;
  tagline: string;
  blurb: string;
  accent: string;
}) {
  return (
    <div className="group relative rounded-lg border border-line bg-panel p-5 shadow-soft-sm transition-all hover:-translate-y-1 hover:shadow-soft">
      <div className="mb-3 flex items-center gap-3">
        <svg width="40" height="46" viewBox="0 0 40 46" aria-hidden className="shrink-0">
          <polygon
            points="20,3 37,12 37,34 20,43 3,34 3,12"
            fill="rgb(var(--c-panel-2))"
            stroke={`rgb(${accent})`}
            strokeWidth="2"
          />
          <polygon
            points="20,8 32,15 32,31 20,38 8,31 8,15"
            fill="none"
            stroke={`rgb(${accent})`}
            strokeWidth="0.8"
            opacity="0.5"
          />
          <circle cx="20" cy="23" r="5.5" fill={`rgb(${accent})`} opacity="0.9" />
        </svg>
        <div className="min-w-0 flex-1">
          <h4 className="text-[13px] font-extrabold tracking-tight text-fg">{name}</h4>
          <p className="mt-0.5 font-mono text-[10px] text-muted">{tagline}</p>
        </div>
      </div>
      <p className="text-[12px] leading-relaxed text-fg-2">{blurb}</p>
    </div>
  );
}

// ── Tier ladder section ─────────────────────────────────────────────────

function TierLadderSection() {
  const tiers = [
    { tier: 'I',   name: 'Mining',    blurb: 'A drill, a smelter, a silo.', goods: 'Iron · Coal · Ingots',                   color: 'var(--c-muted)' },
    { tier: 'II',  name: 'Refining',  blurb: 'Copper, fluids, biotech.',    goods: 'Plates · Fuel · Cultures',               color: 'var(--f-tycho)' },
    { tier: 'III', name: 'Industry',  blurb: 'Plasma, wafers, pharma.',     goods: 'Superalloy · Plasma Cell · Antibiotics', color: 'var(--c-accent)' },
    { tier: 'IV',  name: 'Cohesion',  blurb: 'Networked storage. T4 tech.', goods: 'Quantum Crystal · Neural Substrate',     color: 'var(--f-vega)' },
    { tier: 'V',   name: 'Resonance', blurb: 'Singularity-tier endgame.',   goods: 'Aeon Cell · Transcendent Cortex',        color: 'var(--c-bad)' },
  ];
  return (
    <section className="relative mx-auto max-w-7xl px-6 py-20">
      <SectionHeader
        eyebrow="The long climb"
        title="From a mining drill to a singularity forge."
        body="Five tiers of resources, machines, and contracts. Every tier you unlock keeps the previous one running — late-game charters depend on every step of the chain."
      />
      <div className="relative mt-14">
        {/* Connecting rail behind the ladder */}
        <div
          aria-hidden
          className="absolute left-6 right-6 top-9 h-px"
          style={{
            background:
              'linear-gradient(90deg, rgb(var(--c-muted)) 0%, rgb(var(--f-tycho)) 25%, rgb(var(--c-accent)) 50%, rgb(var(--f-vega)) 75%, rgb(var(--c-bad)) 100%)',
            opacity: 0.4,
          }}
        />
        <div className="relative grid grid-cols-2 gap-4 md:grid-cols-5">
          {tiers.map((t) => (
            <div key={t.tier} className="flex flex-col items-center text-center">
              <div
                className="relative flex h-[72px] w-[72px] items-center justify-center rounded-xl text-[26px] font-extrabold shadow-soft-sm"
                style={{
                  border: `2px solid rgb(${t.color})`,
                  background: 'rgb(var(--c-panel))',
                  color: `rgb(${t.color})`,
                }}
              >
                <span>{t.tier}</span>
              </div>
              <h4 className="mt-3 text-[12px] font-bold uppercase tracking-[0.1em]" style={{ color: `rgb(${t.color})` }}>
                {t.name}
              </h4>
              <p className="mt-1 font-mono text-[10px] text-fg-2">{t.blurb}</p>
              <p className="mt-1.5 font-mono text-[9.5px] text-muted">{t.goods}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Final CTA ───────────────────────────────────────────────────────────

function FinalCtaSection() {
  return (
    <section className="relative mx-auto max-w-5xl px-6 py-24">
      <div className="relative overflow-hidden rounded-xl border border-t-[3px] border-line border-t-accent bg-panel p-10 text-center shadow-soft md:p-14">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-accent">
          Charter authority · awaiting your signature
        </p>
        <h2 className="text-[40px] font-extrabold leading-tight tracking-tight text-fg sm:text-[52px]">
          The first ingot is on the house.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-[14px] text-fg-2">
          Free to play. Your station ticks even after you log out. Sign your
          charter, place a drill, and watch the credits roll in.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/signup"
            className="rounded-md bg-accent px-8 py-3 text-[15px] font-bold tracking-wide text-white transition-all hover:-translate-y-0.5 hover:brightness-105"
          >
            Sign your charter
          </Link>
          <Link
            to="/login"
            className="rounded-md border border-line px-6 py-3 text-[13px] font-bold tracking-wide text-fg transition-colors hover:border-line-strong"
          >
            Returning · sign in
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Footer ──────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="relative mt-10 border-t border-line bg-panel">
      <nav
        aria-label="Footer"
        className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-3 px-6 py-8"
      >
        <div className="flex items-center gap-3">
          <Seal size={28} />
          <span className="text-[14px] font-extrabold tracking-tight text-fg">
            Stellar Freight Co.
          </span>
        </div>
        <span className="hidden text-line md:inline">│</span>
        <Link to="/leaderboards" className="text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2 transition-colors hover:text-accent">
          Leaderboards
        </Link>
        <Link to="/corps" className="text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2 transition-colors hover:text-accent">
          Corps directory
        </Link>
        <Link to="/login" className="text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2 transition-colors hover:text-accent">
          Sign in
        </Link>
        <Link to="/signup" className="text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2 transition-colors hover:text-accent">
          Sign up
        </Link>
        <span className="ml-auto font-mono text-[10px] text-muted">
          CTA Charter Authority · Rim Sector · 2671 ORB
        </span>
      </nav>
    </footer>
  );
}

// ── Section header helper ───────────────────────────────────────────────

function SectionHeader({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <div className="max-w-3xl">
      <p className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-accent">
        <span className="inline-block h-px w-6 bg-accent/60" />
        {eyebrow}
      </p>
      <h2 className="text-[32px] font-extrabold leading-tight tracking-tight text-fg sm:text-[40px] md:text-[48px]">
        {title}
      </h2>
      <p className="mt-4 text-[14px] leading-relaxed text-fg-2">{body}</p>
    </div>
  );
}
